// src/pages/PainelCliente/CatalogoPremios.js
// Catálogo com filtros laterais (gaveta no celular), etiquetas de filtros
// ativos, "só o que cabe no meu saldo" e barra fixa do carrinho.
// Categoria, ordenação e busca ficam na URL (?categoria=&ordenacao=&q=),
// então os links da Home continuam funcionando.

import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Gift, Minus, Plus, Search, ShoppingCart, SlidersHorizontal, X, Zap } from "lucide-react";
import axios from "../../axiosConfig";
import { CarrinhoContext } from "../../CarrinhoContext";
import ModalPremioDetalhe from "../../components/ModalPremioDetalhe";
import {
  calcularPontosPremio,
  estaEmPromocao,
  exigeVoltagem,
  formatarPontos,
  getImagemUrl,
  lerUsuario,
  normalizarTexto,
  pontosSemPromocao,
  premioDisponivel,
} from "../../utils/tikpontos";
import "./CatalogoPremios.css";

const POR_PAGINA = 24;

const FAIXAS = [
  { id: "todas", nome: "Todas", min: null, max: null },
  { id: "ate5", nome: "Até 5 mil", min: null, max: 5000 },
  { id: "5a15", nome: "5 a 15 mil", min: 5001, max: 15000 },
  { id: "mais15", nome: "Acima de 15 mil", min: 15001, max: null },
];

const ORDENACOES = [
  { id: "recomendados", nome: "Recomendados" },
  { id: "menor", nome: "Menor pontuação" },
  { id: "maior", nome: "Maior pontuação" },
  { id: "recentes", nome: "Mais recentes" },
  { id: "mais_pedidos", nome: "Mais pedidos" },
  { id: "nome", nome: "Nome (A a Z)" },
];

function Interruptor({ ligado }) {
  return (
    <span className={`ct-switch ${ligado ? "is-ligado" : ""}`} aria-hidden="true">
      <span />
    </span>
  );
}

function PainelFiltros({
  saldo,
  soSaldo,
  setSoSaldo,
  categorias,
  categoria,
  setCategoria,
  faixa,
  setFaixa,
  minPontos,
  maxPontos,
  setMinPontos,
  setMaxPontos,
  soPromo,
  setSoPromo,
  soFrete,
  setSoFrete,
  limpar,
  tourSaldo,
}) {
  return (
    <div className="ct-filtros-conteudo">
      <button
        type="button"
        className={`ct-saldo-toggle ${soSaldo ? "is-ligado" : ""}`}
        aria-pressed={soSaldo}
        onClick={() => setSoSaldo(!soSaldo)}
        data-tour={tourSaldo ? "ct-saldo" : undefined}
      >
        <span className="ct-saldo-textos">
          <strong>Só o que cabe no meu saldo</strong>
          <span>Até {formatarPontos(saldo)} pts</span>
        </span>
        <Interruptor ligado={soSaldo} />
      </button>

      <fieldset className="ct-grupo">
        <legend>Categorias</legend>
        <div className="ct-categorias">
          {categorias.map((c) => (
            <button
              key={c.nome || "todas"}
              type="button"
              className={`ct-categoria ${categoria === c.nome ? "is-ativa" : ""}`}
              aria-pressed={categoria === c.nome}
              onClick={() => setCategoria(c.nome)}
            >
              <span>{c.rotulo}</span>
              <span className="ct-categoria-qtd">{c.qtd}</span>
            </button>
          ))}
        </div>
      </fieldset>

      <fieldset className="ct-grupo">
        <legend>Faixa de pontos</legend>
        <div className="ct-faixas">
          {FAIXAS.map((f) => (
            <button
              key={f.id}
              type="button"
              className="tk-chip"
              aria-pressed={faixa === f.id}
              onClick={() => {
                setFaixa(f.id);
                setMinPontos("");
                setMaxPontos("");
              }}
            >
              {f.nome}
            </button>
          ))}
        </div>
        <div className="ct-faixa-livre">
          <label className="tk-campo">
            Mínimo
            <input
              className="tk-input"
              type="number"
              inputMode="numeric"
              min="0"
              placeholder="0"
              value={minPontos}
              onChange={(e) => {
                setMinPontos(e.target.value);
                setFaixa("personalizada");
              }}
            />
          </label>
          <label className="tk-campo">
            Máximo
            <input
              className="tk-input"
              type="number"
              inputMode="numeric"
              min="0"
              placeholder="Sem limite"
              value={maxPontos}
              onChange={(e) => {
                setMaxPontos(e.target.value);
                setFaixa("personalizada");
              }}
            />
          </label>
        </div>
      </fieldset>

      <fieldset className="ct-grupo">
        <legend>Outros</legend>
        <label className="ct-check">
          <input type="checkbox" checked={soPromo} onChange={(e) => setSoPromo(e.target.checked)} />
          Em promoção
        </label>
        <label className="ct-check">
          <input type="checkbox" checked={soFrete} onChange={(e) => setSoFrete(e.target.checked)} />
          Frete grátis
        </label>
      </fieldset>

      <button type="button" className="tk-btn tk-btn--secundario tk-btn--bloco ct-limpar" onClick={limpar}>
        Limpar filtros
      </button>
    </div>
  );
}

function CartaoPremio({ premio, pts, ptsOriginais, saldo, quantidade, setQuantidade, onAbrir, onAdicionar, tour }) {
  const promo = estaEmPromocao(premio) && ptsOriginais > pts;
  const cabe = pts <= saldo;
  const pct = pts > 0 ? Math.min(100, Math.round((saldo / pts) * 100)) : 0;

  return (
    <article className="ct-card" data-tour={tour ? "ct-card" : undefined}>
      <button type="button" className="ct-card-abrir" onClick={() => onAbrir(premio)}>
        <span className="ct-card-foto">
          {premio.imagem ? (
            <img src={getImagemUrl(premio.imagem)} alt="" loading="lazy" />
          ) : (
            <Gift size={36} aria-hidden="true" />
          )}
          <span className="ct-card-selos">
            {promo && <span className="ct-selo is-promo">Promoção</span>}
            {premio.frete_gratis ? <span className="ct-selo is-frete">Frete grátis</span> : null}
          </span>
        </span>
        {premio.categoria && <span className="ct-card-categoria">{premio.categoria}</span>}
        <span className="ct-card-nome">{premio.nome}</span>
      </button>

      <div className="ct-card-preco">
        <strong>{formatarPontos(pts)} pts</strong>
        {promo && <s>{formatarPontos(ptsOriginais)}</s>}
      </div>

      {cabe ? (
        <span className="ct-card-cabe">Você já pode resgatar</span>
      ) : (
        <div className="ct-card-falta">
          <span>
            Faltam <strong>{formatarPontos(pts - saldo)} pts</strong>
          </span>
          <div className="tk-progresso" aria-hidden="true">
            <span style={{ width: `${pct}%` }} />
          </div>
        </div>
      )}

      {exigeVoltagem(premio) && (
        <span className="ct-card-volt">
          <Zap size={14} aria-hidden="true" />
          {premio.voltagens_disponiveis ? `${premio.voltagens_disponiveis} (escolha no carrinho)` : "Escolha a voltagem no carrinho"}
        </span>
      )}

      <div className="ct-card-acoes">
        <div className="ct-qtd" role="group" aria-label={`Quantidade de ${premio.nome}`}>
          <button
            type="button"
            aria-label="Diminuir quantidade"
            disabled={quantidade <= 1}
            onClick={() => setQuantidade(quantidade - 1)}
          >
            <Minus size={15} />
          </button>
          <span aria-live="polite">{quantidade}</span>
          <button type="button" aria-label="Aumentar quantidade" onClick={() => setQuantidade(quantidade + 1)}>
            <Plus size={15} />
          </button>
        </div>
        <button type="button" className="tk-btn tk-btn--primario ct-card-add" onClick={() => onAdicionar(premio, quantidade)}>
          <ShoppingCart size={16} aria-hidden="true" />
          Adicionar
        </button>
      </div>
    </article>
  );
}

function CatalogoPremios() {
  const usuario = useMemo(() => lerUsuario(), []);
  const { adicionarAoCarrinho, carrinho } = useContext(CarrinhoContext);
  const [searchParams, setSearchParams] = useSearchParams();

  const [premios, setPremios] = useState([]);
  const [categoriasApi, setCategoriasApi] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");
  const [saldo, setSaldo] = useState(0);
  const [valorPonto, setValorPonto] = useState(0.03);
  const [quantidades, setQuantidades] = useState({});

  const [faixa, setFaixa] = useState("todas");
  const [minPontos, setMinPontos] = useState("");
  const [maxPontos, setMaxPontos] = useState("");
  const [soSaldo, setSoSaldo] = useState(false);
  const [soPromo, setSoPromo] = useState(false);
  const [soFrete, setSoFrete] = useState(false);
  const [visiveis, setVisiveis] = useState(POR_PAGINA);
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);

  const [premioSelecionado, setPremioSelecionado] = useState(null);
  const [modalAberto, setModalAberto] = useState(false);

  // Filtros guardados na URL
  const categoria = searchParams.get("categoria") || "";
  const busca = searchParams.get("q") || "";
  const ordemUrl = searchParams.get("ordenacao") || "recomendados";
  const ordem = ORDENACOES.some((o) => o.id === ordemUrl) ? ordemUrl : "recomendados";

  const setParam = (chave, valor) => {
    const novo = new URLSearchParams(searchParams);
    if (valor) novo.set(chave, valor);
    else novo.delete(chave);
    setSearchParams(novo, { replace: true });
  };

  useEffect(() => {
    let ativo = true;
    async function carregar() {
      setCarregando(true);
      setErro("");
      try {
        const [premiosRes, categoriasRes] = await Promise.all([
          axios.get("/premios"),
          axios.get("/categorias-premios"),
        ]);
        if (!ativo) return;
        setPremios(Array.isArray(premiosRes.data) ? premiosRes.data : []);
        setCategoriasApi((categoriasRes.data || []).filter((c) => c.ativa));
      } catch (err) {
        console.error("Erro ao carregar catálogo:", err);
        if (ativo) setErro("Não foi possível carregar o catálogo. Verifique sua conexão e tente de novo.");
      } finally {
        if (ativo) setCarregando(false);
      }
    }
    carregar();

    axios
      .get("/configuracoes/valor-ponto")
      .then((res) => ativo && res.data?.valor && setValorPonto(res.data.valor))
      .catch(() => {});

    if (usuario?.id) {
      axios
        .get(`/clientes/${usuario.id}/pontos`)
        .then((res) => ativo && setSaldo(Number(res.data?.pontos_disponiveis || 0)))
        .catch(() => {});
    }

    return () => {
      ativo = false;
    };
  }, [usuario?.id]);

  // Volta para a primeira "página" quando um filtro muda
  useEffect(() => {
    setVisiveis(POR_PAGINA);
  }, [busca, categoria, ordem, faixa, minPontos, maxPontos, soSaldo, soPromo, soFrete]);

  // Trava a rolagem com a gaveta de filtros aberta
  useEffect(() => {
    if (!filtrosAbertos) return;
    document.body.style.overflow = "hidden";
    const esc = (e) => e.key === "Escape" && setFiltrosAbertos(false);
    window.addEventListener("keydown", esc);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", esc);
    };
  }, [filtrosAbertos]);

  const pts = (p) => calcularPontosPremio(p, valorPonto);

  const disponiveis = useMemo(() => premios.filter(premioDisponivel), [premios]);

  const termos = useMemo(
    () => normalizarTexto(busca).split(/\s+/).filter((t) => t.length > 1),
    [busca]
  );

  const faixaAtual = FAIXAS.find((f) => f.id === faixa);
  const min = faixa === "personalizada" ? Number(minPontos) || null : faixaAtual?.min ?? null;
  const max = faixa === "personalizada" ? Number(maxPontos) || null : faixaAtual?.max ?? null;

  const passaSemCategoria = (p) => {
    const v = pts(p);
    if (min !== null && v < min) return false;
    if (max !== null && v > max) return false;
    if (soSaldo && v > saldo) return false;
    if (soPromo && !estaEmPromocao(p)) return false;
    if (soFrete && !p.frete_gratis) return false;
    if (termos.length) {
      const texto = normalizarTexto(`${p.nome} ${p.descricao || ""} ${p.categoria || ""}`);
      if (!termos.every((t) => texto.includes(t))) return false;
    }
    return true;
  };

  const semCategoria = disponiveis.filter(passaSemCategoria);
  const filtrados = semCategoria.filter((p) => !categoria || p.categoria === categoria);

  const ordenados = (() => {
    const lista = [...filtrados];
    const data = (p) => new Date(p.data_criacao || 0).getTime();
    const pedidos = (p) => Number(p.total_pedidos || 0);
    switch (ordem) {
      case "menor":
        return lista.sort((a, b) => pts(a) - pts(b));
      case "maior":
        return lista.sort((a, b) => pts(b) - pts(a));
      case "recentes":
        return lista.sort((a, b) => data(b) - data(a));
      case "mais_pedidos":
        return lista.sort((a, b) => pedidos(b) - pedidos(a));
      case "nome":
        return lista.sort((a, b) => String(a.nome).localeCompare(String(b.nome), "pt-BR"));
      default:
        return lista.sort((a, b) => pedidos(b) - pedidos(a) || data(b) - data(a));
    }
  })();

  const categorias = (() => {
    const contar = (nome) => semCategoria.filter((p) => p.categoria === nome).length;
    const comProdutos = categoriasApi.filter((c) => disponiveis.some((p) => p.categoria === c.nome));
    const lista = comProdutos.map((c) => ({ nome: c.nome, rotulo: c.nome, qtd: contar(c.nome) }));
    if (categoria && !lista.some((c) => c.nome === categoria)) {
      lista.push({ nome: categoria, rotulo: categoria, qtd: contar(categoria) });
    }
    return [{ nome: "", rotulo: "Todas", qtd: semCategoria.length }, ...lista];
  })();

  const limpar = () => {
    setFaixa("todas");
    setMinPontos("");
    setMaxPontos("");
    setSoSaldo(false);
    setSoPromo(false);
    setSoFrete(false);
    const novo = new URLSearchParams(searchParams);
    novo.delete("categoria");
    novo.delete("q");
    setSearchParams(novo, { replace: true });
  };

  const etiquetas = [];
  if (categoria) etiquetas.push({ nome: categoria, remover: () => setParam("categoria", "") });
  if (faixa !== "todas") {
    const nome =
      faixa === "personalizada"
        ? `${min ? formatarPontos(min) : "0"} a ${max ? formatarPontos(max) : "sem limite"} pts`
        : faixaAtual?.nome;
    etiquetas.push({
      nome,
      remover: () => {
        setFaixa("todas");
        setMinPontos("");
        setMaxPontos("");
      },
    });
  }
  if (soSaldo) etiquetas.push({ nome: "Cabe no meu saldo", remover: () => setSoSaldo(false) });
  if (soPromo) etiquetas.push({ nome: "Em promoção", remover: () => setSoPromo(false) });
  if (soFrete) etiquetas.push({ nome: "Frete grátis", remover: () => setSoFrete(false) });
  if (busca) etiquetas.push({ nome: `“${busca}”`, remover: () => setParam("q", "") });

  const qtdFiltros = etiquetas.filter((e) => !e.nome.startsWith("“")).length;

  const adicionar = (premio, qtd) => {
    adicionarAoCarrinho(premio, qtd);
    toast.success(`"${premio.nome}" adicionado ao carrinho.`);
    setQuantidades((q) => ({ ...q, [premio.id]: 1 }));
  };

  const abrir = (premio) => {
    setPremioSelecionado(premio);
    setModalAberto(true);
  };

  // Barra do carrinho
  const itensCarrinho = Array.isArray(carrinho) ? carrinho : [];
  const qtdCarrinho = itensCarrinho.reduce((a, i) => a + Number(i.quantidade || 1), 0);
  const ptsCarrinho = itensCarrinho.reduce((a, i) => a + pts(i) * Number(i.quantidade || 1), 0);
  const sobraCarrinho = saldo - ptsCarrinho;

  const propsFiltros = {
    saldo,
    soSaldo,
    setSoSaldo,
    categorias,
    categoria,
    setCategoria: (nome) => setParam("categoria", nome),
    faixa,
    setFaixa,
    minPontos,
    maxPontos,
    setMinPontos,
    setMaxPontos,
    soPromo,
    setSoPromo,
    soFrete,
    setSoFrete,
    limpar,
  };

  return (
    <div className={`ct ${qtdCarrinho > 0 ? "tem-carrinho" : ""}`}>
      <header className="tk-cabecalho-pagina">
        <div>
          <h1 className="tk-titulo-pagina">Catálogo de prêmios</h1>
          <p className="tk-subtitulo-pagina">Troque seus pontos por produtos entregues no endereço da sua empresa.</p>
        </div>
        <div className="ct-saldo">
          <span>Seu saldo</span>
          <strong>{formatarPontos(saldo)} pts</strong>
        </div>
      </header>

      <label className="ct-busca" data-tour="ct-busca">
        <span className="tk-sr-only">Buscar prêmio</span>
        <Search size={20} aria-hidden="true" />
        <input
          type="search"
          value={busca}
          onChange={(e) => setParam("q", e.target.value)}
          placeholder="Buscar por nome ou tipo de produto (ex.: panela, fone, TV)"
          autoComplete="off"
        />
      </label>

      {/* Barra de atalhos do celular */}
      <div className="ct-atalhos-mobile">
        <button
          type="button"
          className="tk-chip is-ativo ct-btn-filtros"
          onClick={() => setFiltrosAbertos(true)}
          data-tour="ct-filtros-mobile"
        >
          <SlidersHorizontal size={16} aria-hidden="true" />
          Filtros{qtdFiltros ? ` (${qtdFiltros})` : ""}
        </button>
        <button
          type="button"
          className="tk-chip"
          aria-pressed={soSaldo}
          onClick={() => setSoSaldo(!soSaldo)}
          data-tour="ct-saldo-mobile"
        >
          Cabe no meu saldo
        </button>
        <button type="button" className="tk-chip" aria-pressed={soPromo} onClick={() => setSoPromo(!soPromo)}>
          Promoções
        </button>
        <button type="button" className="tk-chip" aria-pressed={soFrete} onClick={() => setSoFrete(!soFrete)}>
          Frete grátis
        </button>
      </div>

      <div className="ct-corpo">
        <aside className="ct-lateral" aria-label="Filtros" data-tour="ct-filtros">
          <PainelFiltros {...propsFiltros} tourSaldo />
        </aside>

        <section className="ct-resultados" aria-live="polite">
          <div className="ct-barra">
            <div className="ct-etiquetas">
              <strong>
                {carregando ? "Carregando…" : `${filtrados.length} ${filtrados.length === 1 ? "prêmio" : "prêmios"}`}
              </strong>
              {etiquetas.map((e) => (
                <button key={e.nome} type="button" className="ct-etiqueta" onClick={e.remover}>
                  {e.nome}
                  <X size={14} aria-hidden="true" />
                  <span className="tk-sr-only">Remover filtro</span>
                </button>
              ))}
            </div>
            <label className="ct-ordenar">
              <span>Ordenar por</span>
              <select className="tk-select" value={ordem} onChange={(e) => setParam("ordenacao", e.target.value)}>
                {ORDENACOES.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.nome}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {erro ? (
            <div className="tk-vazio">
              <strong>{erro}</strong>
              <button type="button" className="tk-btn tk-btn--primario" onClick={() => window.location.reload()}>
                Tentar de novo
              </button>
            </div>
          ) : carregando ? (
            <div className="ct-grade">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="tk-skeleton ct-card-skeleton" />
              ))}
            </div>
          ) : ordenados.length === 0 ? (
            <div className="tk-vazio">
              <strong>Nenhum prêmio com esses filtros</strong>
              <span>Remova algum filtro ou busque por outra palavra.</span>
              <button type="button" className="tk-btn tk-btn--primario" onClick={limpar}>
                Limpar filtros
              </button>
            </div>
          ) : (
            <>
              <div className="ct-grade">
                {ordenados.slice(0, visiveis).map((p, i) => (
                  <CartaoPremio
                    key={p.id}
                    premio={p}
                    pts={pts(p)}
                    ptsOriginais={pontosSemPromocao(p, valorPonto)}
                    saldo={saldo}
                    quantidade={quantidades[p.id] || 1}
                    setQuantidade={(q) => setQuantidades((s) => ({ ...s, [p.id]: Math.max(1, Math.min(99, q)) }))}
                    onAbrir={abrir}
                    onAdicionar={adicionar}
                    tour={i === 0}
                  />
                ))}
              </div>

              <div className="ct-mais">
                <span>
                  Mostrando {Math.min(visiveis, ordenados.length)} de {ordenados.length}
                </span>
                {visiveis < ordenados.length && (
                  <button
                    type="button"
                    className="tk-btn tk-btn--secundario"
                    onClick={() => setVisiveis((v) => v + POR_PAGINA)}
                  >
                    Carregar mais prêmios
                  </button>
                )}
              </div>
            </>
          )}
        </section>
      </div>

      {/* Gaveta de filtros (celular) */}
      {filtrosAbertos && (
        <div className="ct-gaveta">
          <button type="button" className="ct-gaveta-fundo" aria-label="Fechar filtros" onClick={() => setFiltrosAbertos(false)} />
          <div className="ct-gaveta-painel" role="dialog" aria-modal="true" aria-labelledby="ct-gaveta-titulo">
            <div className="ct-gaveta-topo">
              <h2 id="ct-gaveta-titulo">Filtros</h2>
              <button type="button" className="ct-gaveta-fechar" aria-label="Fechar" onClick={() => setFiltrosAbertos(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="ct-gaveta-corpo">
              <PainelFiltros {...propsFiltros} />
            </div>
            <div className="ct-gaveta-rodape">
              <button type="button" className="tk-btn tk-btn--primario tk-btn--bloco" onClick={() => setFiltrosAbertos(false)}>
                Ver {filtrados.length} {filtrados.length === 1 ? "prêmio" : "prêmios"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Barra fixa do carrinho */}
      {qtdCarrinho > 0 && (
        <div className="ct-carrinho" data-tour="ct-carrinho">
          <div className="ct-carrinho-textos">
            <strong>
              {qtdCarrinho} {qtdCarrinho === 1 ? "item" : "itens"} no carrinho · {formatarPontos(ptsCarrinho)} pts
            </strong>
            <span className={sobraCarrinho >= 0 ? "is-ok" : "is-falta"}>
              {sobraCarrinho >= 0
                ? `Sobram ${formatarPontos(sobraCarrinho)} pts (frete calculado no carrinho)`
                : `Faltam ${formatarPontos(-sobraCarrinho)} pts. Remova algum item.`}
            </span>
          </div>
          <Link to="/painel-cliente/carrinho-resgates" className="tk-btn ct-carrinho-btn">
            Ver carrinho
          </Link>
        </div>
      )}

      <ModalPremioDetalhe
        premio={premioSelecionado}
        aberto={modalAberto}
        onClose={() => setModalAberto(false)}
        onAdicionar={(premio, qtd) => {
          adicionar(premio, qtd);
          setModalAberto(false);
        }}
        pontosProduto={premioSelecionado ? pts(premioSelecionado) : 0}
        pontosOriginais={premioSelecionado ? pontosSemPromocao(premioSelecionado, valorPonto) : 0}
        saldoDisponivel={saldo}
      />
    </div>
  );
}

export default CatalogoPremios;
